/***
 *
 *   USERS
 *   Enables an admin to manage the users in their organization
 *
 **********/

import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, ViewContext, Card, AccountNav, Animate, Search } from 'components/lib';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { UMButton, UMBadge, UMTable } from '@urbanmetrix/um-kit';
import type { UMSharedIconProps, UMTableColumn } from '@urbanmetrix/um-kit';
import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@mui/material';
import Mail01 from '@untitled-ui/icons-react/build/cjs/Mail01';
import { DeleteIcon, EditIcon, WarningIcon, AlertIcon } from 'components/icons';
import { getProjects } from '../../api/projectApi';
import { getCurrentUsage } from '../../api/planApi';
import { getInviteEmailValidation } from '../../services/inviteEmailValidation';
import {
  cancelOrganizationInvitation,
  inviteOrganizationMember,
  listOrganizationInvitations,
  listOrganizationMembers,
  removeOrganizationMember,
  updateOrganizationMemberRole,
} from '../../lib/auth-client';
import { getApiErrorMessage } from 'api/apiClient';

const ORGANIZATION_ROLE_OPTIONS = [
  { value: 'admin', label: 'admin' },
  { value: 'editor', label: 'editor' },
  { value: 'viewer', label: 'viewer' },
];

interface InviteForm {
  email: string;
  permission: string;
  projects: string[];
}

const DEFAULT_INVITE_FORM: InviteForm = {
  email: '',
  permission: 'editor',
  projects: [],
};

type AddUserIconProps = UMSharedIconProps;

interface OrganizationMember {
  id: string;
  userId: string;
  role: string;
  createdAt?: string | Date;
  user?: {
    name?: string;
    email?: string;
  };
}

interface OrganizationInvitation {
  id: string;
  email: string;
  role?: string;
  status: string;
  createdAt?: string | Date;
}

interface UserRow {
  rowId: string;
  id: string;
  userId?: string;
  kind: 'member' | 'invitation';
  name: string;
  email: string;
  date_created?: string | Date;
  permission: string;
  status: string;
}

interface ProjectSummary {
  id: string;
  name: string;
}

interface UserUsage {
  usersUsed: number | null;
  usersLimit: number | null;
}

interface ProjectOption {
  name: string;
  value: string;
}

interface UsersApiError {
  message?: string;
  error?: { message?: string };
  response?: {
    status?: number;
    data?: { message?: string };
  };
}

interface InviteChangePayload {
  input: string;
  value: string | string[];
}

interface InviteSubmitData {
  email: string;
  permission: 'owner' | 'admin' | 'editor' | 'viewer';
  projects?: ProjectOption[];
}

interface UpdateUserSubmitData {
  permission: string;
}

const AddUserWarningIcon = ({ size, className }: AddUserIconProps) => (
  <WarningIcon className={className} size={size === undefined ? undefined : Number(size)} color="#ca8a04" />
);

const AddUserAlertIcon = ({ size, className }: AddUserIconProps) => (
  <AlertIcon className={className} size={size === undefined ? undefined : Number(size)} color="#dc2626" />
);

type UsersProps = Record<string, never>;

export function Users(_props: UsersProps) {
  const context = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteForm>(DEFAULT_INVITE_FORM);
  const [invitePermission, setInvitePermission] = useState(DEFAULT_INVITE_FORM.permission);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [userUsage, setUserUsage] = useState<UserUsage | null>(null);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);

  function getErrorMessage(error: unknown, fallback = 'An error occurred. Please try again.') {
    return getApiErrorMessage(error, fallback);
  }

  function toFormError(error: unknown, fallback: string) {
    const err = (error ?? {}) as UsersApiError;
    return {
      response: {
        status: err?.response?.status || 400,
        data: {
          message: getErrorMessage(error, fallback),
        },
      },
    };
  }

  function createInputError(input: string, message: string) {
    const error = new Error(message) as Error & {
      response: {
        status: number;
        data: { inputError: string; message: string };
      };
    };
    error.response = {
      status: 400,
      data: {
        inputError: input,
        message,
      },
    };

    return error;
  }

  async function loadUsers() {
    if (!authContext.user?.session?.activeOrganizationId) {
      setRows([]);
      return;
    }

    try {
      setLoading(true);

      const [membersResponse, invitationsResponse] = await Promise.all([
        listOrganizationMembers(),
        listOrganizationInvitations(),
      ]);

      const members: OrganizationMember[] = membersResponse?.members || [];
      const memberRows: UserRow[] = members.map((member) => ({
        rowId: `member-${member.id}`,
        id: member.id,
        userId: member.userId,
        kind: 'member',
        name: member.user?.name || '',
        email: member.user?.email || '',
        date_created: member.createdAt,
        permission: member.role,
        status: 'Registered',
      }));

      const invitations: OrganizationInvitation[] = invitationsResponse || [];
      const invitationRows: UserRow[] = invitations
        .filter((invitation) => invitation.status === 'pending')
        .map((invitation) => ({
          rowId: `invitation-${invitation.id}`,
          id: invitation.id,
          kind: 'invitation',
          name: '',
          email: invitation.email,
          date_created: invitation.createdAt,
          permission: invitation.role || 'editor',
          status: 'Invited',
        }));

      setRows([...memberRows, ...invitationRows]);
    } catch (error) {
      context.handleError(error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, [authContext.user?.session?.activeOrganizationId]);

  useEffect(() => {
    let mounted = true;

    async function loadProjects() {
      try {
        const response = await getProjects();
        if (!mounted) return;

        setProjects(response || []);
      } catch (error) {
        if (!mounted) return;
        setProjects([]);
      }
    }

    loadProjects();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadUsage() {
      if (!authContext.user?.session?.activeOrganizationId) {
        if (mounted) setUserUsage(null);
        return;
      }

      await refreshUserUsage(() => mounted);
    }

    loadUsage();

    return () => {
      mounted = false;
    };
  }, [authContext.user?.session?.activeOrganizationId]);

  async function refreshUserUsage(isMountedCheck = () => true) {
    try {
      const usage = await getCurrentUsage();
      if (!isMountedCheck()) return;

      setUserUsage({
        usersUsed: usage?.usersUsed ?? null,
        usersLimit: usage?.usersLimit ?? null,
      });
    } catch (error) {
      if (!isMountedCheck()) return;
      setUserUsage(null);
    }
  }

  const roleOptions = ORGANIZATION_ROLE_OPTIONS.filter((option) => option.value !== 'owner');
  const projectOptions = useMemo<ProjectOption[]>(() => {
    return (projects || []).map((project) => ({
      name: project.name,
      value: project.id,
    }));
  }, [projects]);
  const usersUsed = Number(userUsage?.usersUsed);
  const usersLimit = Number(userUsage?.usersLimit);
  const hasUserLimit = Number.isFinite(usersUsed) && Number.isFinite(usersLimit);
  const remainingUsers = hasUserLimit ? usersLimit - usersUsed : null;
  const hasNoUsersAvailable = remainingUsers !== null && remainingUsers <= 0;
  const isLastUserAvailable = remainingUsers === 1;
  const addUserTooltipTitle = hasNoUsersAvailable
    ? 'No users are available on your current plan. Upgrade your plan to continue.'
    : isLastUserAvailable
      ? 'Only one user is still available on your plan. Upgrade your plan if you need to invite more users.'
      : '';

  function handleInviteChange({ input, value }: InviteChangePayload) {
    setInviteForm((current) => {
      const next = {
        ...current,
        [input]: value,
      };

      if (input === 'permission' && value === 'admin') {
        next.projects = [];
      }

      return next;
    });

    if (input === 'permission' && typeof value === 'string') {
      setInvitePermission(value);
    }
  }

  function buildInviteModalConfig() {
    const inviteValidation = getInviteEmailValidation(inviteForm.email);
    const enteredInviteCount = inviteValidation.emails.length;
    const hasInvalidInviteEmails = inviteValidation.hasInvalidEmails;
    const exceedsInviteLimit = remainingUsers !== null && enteredInviteCount > remainingUsers;
    const modalErrorMessage = hasInvalidInviteEmails
      ? 'Please enter valid email addresses.'
      : exceedsInviteLimit
        ? `You can invite up to ${remainingUsers} more user${remainingUsers === 1 ? '' : 's'} with your current plan. Reduce the number of email addresses before proceeding, or upgrade your plan if you need more users.`
        : null;

    return {
      title: 'Add User',
      text: 'To invite more than one user, separate the emails with a comma.',
      form: {
        email: {
          label: 'Email',
          type: 'text',
          required: true,
          value: inviteForm.email,
        },
        permission: {
          label: (
            <>
              Permission
              <CustomTooltip
                title={
                  <>
                    Editors can be internal or external collaborators of the organization. They can view and edit only
                    the specific projects they have been granted access to by an Admin.
                    <br />
                    Admins have access to all projects within the organization. They can create new projects, invite
                    users, and manage project access.
                    <br />
                    Billing features remain restricted to the Organization Owner.
                  </>
                }
              />
            </>
          ),
          type: 'select',
          default: invitePermission,
          value: invitePermission,
          options: roleOptions,
        },
        ...(invitePermission !== 'admin' && {
          projects: {
            label: 'Project Access',
            type: 'checkbox',
            options: projectOptions,
            cols: 1,
            selectAll: { label: 'SELECT ALL' },
            value: inviteForm.projects,
          },
        }),
      },
      preSubmitText: modalErrorMessage,
      preSubmitClassName: modalErrorMessage ? 'font-semibold mt-2 mb-0' : undefined,
      preSubmitStyle: modalErrorMessage ? { color: '#dc2626' } : undefined,
      buttonText: 'Send Invite',
      disabled: Boolean(modalErrorMessage),
      customSubmit: async (data: InviteSubmitData) => {
        try {
          const validation = getInviteEmailValidation(data.email);
          const emails = validation.validEmails;
          const hasInvalidEmails = validation.hasInvalidEmails;
          const inviteCount = validation.emails.length;
          const exceedsLimit = remainingUsers !== null && inviteCount > remainingUsers;

          if (hasInvalidEmails) {
            throw createInputError('email', 'Please enter valid email addresses.');
          }

          if (exceedsLimit) {
            throw createInputError(
              'email',
              `You can invite up to ${remainingUsers} more user${remainingUsers === 1 ? '' : 's'} with your current plan.`
            );
          }

          const selectedProjects = Array.isArray(data.projects) ? data.projects : [];

          const invitations = [];

          for (const email of emails) {
            const invitation = await inviteOrganizationMember(
              email,
              data.permission,
              selectedProjects.map((project) => project.value)
            );
            invitations.push(invitation);
          }

          setInviteForm(DEFAULT_INVITE_FORM);
          setInvitePermission(DEFAULT_INVITE_FORM.permission);

          return {
            message: emails.length > 1 ? 'Invitations sent' : 'Invitation sent',
            data: invitations,
          };
        } catch (error) {
          throw toFormError(error, 'Failed to send invitation.');
        }
      },
      onChange: handleInviteChange,
    };
  }

  function invite() {
    setInviteForm(DEFAULT_INVITE_FORM);
    setInvitePermission(DEFAULT_INVITE_FORM.permission);

    context.modal.show(buildInviteModalConfig(), async () => {
      await loadUsers();
      await refreshUserUsage();
    });
  }

  useEffect(() => {
    if (context.modal.data?.show && context.modal.data?.title === 'Add User') {
      context.modal.refresh({
        ...context.modal.data,
        ...buildInviteModalConfig(),
      });
    }
  }, [invitePermission, inviteForm.email, inviteForm.projects, projectOptions, remainingUsers]);

  function editUser(row: UserRow) {
    context.modal.show(
      {
        title: 'Update User',
        form: {
          permission: {
            label: 'Permission',
            type: row.permission === 'owner' ? null : 'select',
            default: row.permission,
            value: row.permission,
            options: roleOptions,
          },
        },
        buttonText: 'Save',
        customSubmit: async (data: UpdateUserSubmitData) => {
          try {
            const response = await updateOrganizationMemberRole(
              row.id,
              data.permission,
              authContext.user?.session?.activeOrganizationId ?? undefined
            );

            return {
              message: `${row.email} was updated`,
              data: response?.member || response,
            };
          } catch (error) {
            throw toFormError(error, 'Failed to update user.');
          }
        },
      },
      async () => {
        await loadUsers();
        await refreshUserUsage();
      }
    );
  }

  function deleteUser(row: UserRow) {
    context.modal.show(
      {
        title: 'Remove User',
        form: {},
        buttonText: 'Remove User',
        text: `Are you sure you want to remove ${row.name || row.email} from the current organization?`,
        destructive: true,
        customSubmit: async () => {
          try {
            const response = await removeOrganizationMember(
              row.id,
              authContext.user?.session?.activeOrganizationId ?? undefined
            );

            return {
              message: `${row.name || row.email} was removed`,
              data: response?.member || response,
            };
          } catch (error) {
            throw toFormError(error, 'Failed to remove user.');
          }
        },
      },
      async () => {
        await loadUsers();
        await refreshUserUsage();
      }
    );
  }

  function deleteInvite(row: UserRow) {
    context.modal.show(
      {
        title: 'Delete Invite',
        form: {},
        buttonText: 'Delete Invite',
        text: `Are you sure you want to delete the invite for ${row.email}?`,
        destructive: true,
        customSubmit: async () => {
          try {
            const response = await cancelOrganizationInvitation(row.id);

            return {
              message: `${row.email}'s invite was deleted`,
              data: response?.invitation || response,
            };
          } catch (error) {
            throw toFormError(error, 'Failed to delete invitation.');
          }
        },
      },
      async () => {
        await loadUsers();
        await refreshUserUsage();
      }
    );
  }

  async function resendInvite(row: UserRow) {
    try {
      await inviteOrganizationMember(row.email, row.permission as 'owner' | 'admin' | 'editor' | 'viewer', [], true);
      context.notification.show(`Invite re-sent to ${row.email}`, 'success', true, 'toast');
      await loadUsers();
      await refreshUserUsage();
    } catch (error) {
      context.handleError(error);
    }
  }

  const formatDate = (value?: string | Date | null): string => {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toISOString().replace('T', ' ').split('.')[0];
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return rows;

    const term = searchTerm.toLowerCase();

    return rows.filter((row) => {
      const values = [row.name, row.email, row.date_created, row.permission, row.status];
      return values.some((value) => value && value.toString().toLowerCase().includes(term));
    });
  }, [rows, searchTerm]);

  const columns = useMemo<UMTableColumn<UserRow>[]>(() => {
    return [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      {
        key: 'date_created',
        label: 'Date Created',
        sortable: true,
        sortAccessor: (row: UserRow) => row.date_created,
        render: (row: UserRow) => <span className="users-table-date">{formatDate(row.date_created)}</span>,
      },
      { key: 'permission', label: 'Permission', sortable: true },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (row: UserRow) => {
          const status = row.status?.toLowerCase();
          const badgeColor = status === 'registered' ? 'green' : 'blue';
          return <UMBadge text={row.status} color={badgeColor} />;
        },
      },
      {
        key: 'actions',
        label: 'Actions',
        align: 'center',
        render: (row: UserRow) => {
          const isInvited = row.kind === 'invitation';
          const isOwner = row.permission === 'owner';

          return (
            <div className="users-table-actions">
              {isInvited ? (
                <UMButton
                  icon={Mail01}
                  iconColor="#3b3b3b"
                  title="Resend Invite"
                  iconSize={16}
                  onClick={() => resendInvite(row)}
                  className="users-action-btn"
                />
              ) : (
                !isOwner && (
                  <UMButton
                    icon={EditIcon}
                    iconColor="#16a34a"
                    title="Edit"
                    color="#16a34a"
                    iconSize={16}
                    onClick={() => editUser(row)}
                    className="users-action-btn"
                  />
                )
              )}

              {!isInvited && (
                <UMButton
                  icon={Mail01}
                  iconColor="#3b3b3b"
                  title="Email"
                  iconSize={16}
                  onClick={() => (window.location.href = `mailto:${row.email}`)}
                  className="users-action-btn"
                />
              )}

              {(!isOwner || isInvited) && (
                <UMButton
                  icon={DeleteIcon}
                  iconColor="#e11d48"
                  title="Delete"
                  color="#e11d48"
                  iconSize={16}
                  onClick={() => (isInvited ? deleteInvite(row) : deleteUser(row))}
                  className="users-action-btn"
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [rows]);

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <Tooltip title={addUserTooltipTitle} disableHoverListener={!addUserTooltipTitle}>
            <span style={{ display: 'inline-flex' }}>
              <UMButton
                small
                text="Add User"
                icon={hasNoUsersAvailable ? AddUserAlertIcon : isLastUserAvailable ? AddUserWarningIcon : undefined}
                onClick={() => {
                  if (hasNoUsersAvailable) {
                    setLimitDialogOpen(true);
                    return;
                  }

                  invite();
                }}
              />
            </span>
          </Tooltip>
        </div>

        <Card extraClass="form-container" isStat={true}>
          <div className="users-table-wrap">
            <Search className="users-table-search" callback={setSearchTerm} throttle={200} />
            <UMTable
              columns={columns}
              rows={filteredUsers}
              loading={loading}
              getRowId={(row) => row.rowId}
              defaultSort={{ key: 'date_created', direction: 'desc' }}
            />
          </div>
        </Card>

        <Dialog
          open={limitDialogOpen}
          onClose={() => setLimitDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          slotProps={{
            paper: {
              sx: {
                backgroundColor: '#ececec',
              },
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#ececec' }}>User Limit Reached</DialogTitle>
          <DialogContent sx={{ backgroundColor: '#ececec' }}>
            <Typography sx={{ color: '#4b5563', marginBottom: '12px' }}>
              You have no users left on your current plan.
            </Typography>
            <Typography sx={{ color: '#4b5563' }}>
              The recommended option is to upgrade your plan if you need to invite more users, or delete existing active
              users to add new ones.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '0 24px 24px', backgroundColor: '#ececec' }}>
            <UMButton text="Cancel" onClick={() => setLimitDialogOpen(false)} />
            <UMButton
              className="modal-yes-btn"
              text="Go to billing page"
              onClick={() => {
                setLimitDialogOpen(false);
                navigate('/account/subscription');
              }}
            />
          </DialogActions>
        </Dialog>
      </Animate>
    </Fragment>
  );
}
